import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractsApi, type ContractOwner } from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { easternTodayYmd } from '@/modules/manpower/lib/gridDates';
import DateField from './DateField';
import {
  emptyOwnerForm, ownerFormFrom, validateOwnerForm, ownerCreateBody, ownerPatch, type OwnerForm,
} from './contractsModel';

/**
 * Add an owner contract, or change / remove one. A contract can be under review
 * before it has a job, so "No job yet" is a real choice, and a job can be linked
 * later. Saving sends only the fields that changed (`ownerPatch`).
 */
export default function OwnerContractModal({ row, fixedJob, onClose }: {
  /** The contract being changed; omitted to add one. */
  row?: ContractOwner;
  /** On a job's own tab the contract is for that job. */
  fixedJob?: { id: number; label: string };
  onClose: () => void;
}) {
  const editing = !!row;
  const qc = useQueryClient();
  // Eastern: the clock the API checks "not in the future" against (see SubcontractModal).
  const today = easternTodayYmd();

  const [initial] = useState<OwnerForm>(() => (row ? ownerFormFrom(row) : emptyOwnerForm(fixedJob ? String(fixedJob.id) : '')));
  const [form, setForm] = useState<OwnerForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof OwnerForm>(key: K, value: OwnerForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const optionsQ = useQuery({
    queryKey: ['contracts', 'project-options'],
    queryFn: () => contractsApi.projectOptions(),
    staleTime: 5 * 60_000,
    enabled: !fixedJob,
  });
  // Every job the caller can see, placeholder numbers included: a contract is
  // often under review before the job has its real number.
  const jobs = optionsQ.data ?? [];
  // A contract linked to a job that isn't in the list (an archived one) must
  // still show it, or the picker would read "No job yet" and lie.
  const linkedMissing = !!row && row.projectId !== null && !jobs.some((o) => o.id === row.projectId);

  const finish = () => { void qc.invalidateQueries({ queryKey: ['contracts'] }); onClose(); };
  const fail = (e: unknown) => setError((e as Error)?.message || 'That did not save. Try again.');

  const save = useMutation({
    mutationFn: () => (row
      ? contractsApi.updateOwner(row.id, ownerPatch(initial, form))
      : contractsApi.createOwner(ownerCreateBody(form))),
    onSuccess: finish,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => contractsApi.removeOwner(row!.id),
    onSuccess: finish,
    onError: fail,
  });
  const busy = save.isPending || remove.isPending;

  const onSave = () => {
    const problem = validateOwnerForm(form, today);
    if (problem) { setError(problem); return; }
    if (editing && Object.keys(ownerPatch(initial, form)).length === 0) { onClose(); return; }
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={editing ? 'Edit owner contract' : 'Add owner contract'}
      maxWidth={720}
      closeOnBackdropClick={!busy}
      footer={(
        <>
          {row && (
            <span className="ct-modal-extra">
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  if (window.confirm('Remove this owner contract? Only do this for one keyed in by mistake — a finished one should be marked completed.')) remove.mutate();
                }}
              >
                Remove
              </Button>
            </span>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={onSave} isLoading={save.isPending} disabled={busy}>Save</Button>
        </>
      )}
    >
      <div className="ct-form">
        <label className="ct-field">
          <span>Contract / client</span>
          <input
            value={form.name}
            maxLength={300}
            placeholder="Client — project — what it is"
            onChange={(e) => set('name', e.target.value)}
          />
        </label>

        <div className="ct-grid2">
          {fixedJob ? (
            <p className="ct-help">Job {fixedJob.label}</p>
          ) : (
            <label className="ct-field">
              <span>Job</span>
              <select value={form.projectId} onChange={(e) => set('projectId', e.target.value)}>
                <option value="">No job yet</option>
                {linkedMissing && row && (
                  <option value={String(row.projectId)}>{row.jobNumber}{row.projectTitle ? ` · ${row.projectTitle}` : ''}</option>
                )}
                {jobs.map((o) => (
                  <option key={o.id} value={o.id}>{o.jobNumber}{o.title ? ` · ${o.title}` : ''}</option>
                ))}
              </select>
              {optionsQ.isError && <span className="ct-help">The job list didn’t load. Close this and try again.</span>}
              <span className="ct-help">Link it once the job exists.</span>
            </label>
          )}
          <label className="ct-field">
            <span>Sent by</span>
            <input value={form.sentBy} maxLength={120} onChange={(e) => set('sentBy', e.target.value)} />
          </label>
          <DateField label="Received" value={form.receivedOn} max={today} onChange={(v) => set('receivedOn', v)} />
          <DateField label="Redline sent" value={form.redlineSentOn} max={today} onChange={(v) => set('redlineSentOn', v)} />
          <DateField label="Approved" value={form.approvedOn} max={today} onChange={(v) => set('approvedOn', v)} />
          <DateField
            label="Completed"
            value={form.completedOn}
            max={today}
            onChange={(v) => set('completedOn', v)}
            help="Takes it off the open list; it drops off the page after 30 days."
          />
        </div>

        <label className="ct-field">
          <span>Notes</span>
          <textarea rows={3} maxLength={1000} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>

        {error && <div className="ct-error" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}
